import React, { useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { W } from "./wizardTokens";

const AI_EXTENSIONS = ["pdf", "doc", "docx", "ppt", "pptx", "xls", "xlsx", "txt", "md"];
const DOC_HINT = "Any file type · up to 25 MB each";
const AI_HINT = "PDF, Word, PowerPoint or Excel · up to 10 files";

function extOf(name) {
  const ext = (name.split(".").pop() || "").toUpperCase().slice(0, 4);
  return ext === "DOCX" ? "DOC" : ext === "PPTX" ? "PPT" : ext === "XLSX" ? "XLS" : ext;
}

function fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function PickStep({ kind, setKind, aiType, setAiType, files, setFiles }) {
  const [over, setOver] = useState(false);
  const inputRef = useRef(null);

  const addFiles = (list) => setFiles((prev) => [...prev, ...list]);
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const onDrop = (e) => {
    e.preventDefault();
    setOver(false);
    addFiles([...e.dataTransfer.files]);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "18px" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "12px" }} role="radiogroup" aria-label="What are you importing?">
        <Box
          role="radio" tabIndex={0} aria-checked={kind === "ai"}
          onClick={() => { if (kind !== "ai") { setKind("ai"); setFiles([]); } }}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); if (kind !== "ai") { setKind("ai"); setFiles([]); } } }}
          sx={{
            textAlign: "left", font: "inherit", color: "inherit", bgcolor: W.surface,
            border: `1.5px solid ${kind === "ai" ? W.ai : W.line}`,
            boxShadow: kind === "ai" ? `inset 0 0 0 1px ${W.ai}` : "none",
            borderRadius: "12px", p: "16px", cursor: "pointer", display: "flex", gap: "12px",
            "&:hover": { bgcolor: kind === "ai" ? W.aiBg : "#faf9fb" },
          }}
        >
          <Box sx={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${kind === "ai" ? W.ai : W.lineCool}`, mt: "2px", flexShrink: 0, display: "grid", placeItems: "center" }}>
            {kind === "ai" && <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: W.ai }} />}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: "15.5px", fontWeight: 600 }}>Skill sheet or test</Typography>
              <Box component="span" sx={{ fontSize: "11.5px", fontWeight: 700, px: "8px", py: "2px", borderRadius: "999px", bgcolor: W.aiBg, color: W.ai, border: `1px solid ${W.aiLine}` }}>AI-assisted</Box>
            </Box>
            <Typography sx={{ fontSize: "13px", color: W.ink3, mt: "3px", lineHeight: 1.45 }}>
              AI reads your file and builds an editable draft. Nothing is saved until you review it.
            </Typography>
            {kind === "ai" && (
              <Box onClick={(e) => e.stopPropagation()} sx={{ display: "inline-flex", p: "3px", mt: "10px", borderRadius: "8px", bgcolor: W.surface2, gap: "2px" }}>
                {["skill-sheet", "test"].map((t) => (
                  <Box key={t} component="button" type="button" aria-pressed={aiType === t} onClick={() => setAiType(t)}
                    sx={{ font: "inherit", fontSize: "12.5px", fontWeight: 600, border: 0, px: "12px", py: "6px", borderRadius: "6px", cursor: "pointer", color: aiType === t ? W.ink : W.ink3, bgcolor: aiType === t ? "#fff" : "transparent", boxShadow: aiType === t ? W.shadowSm : "none" }}>
                    {t === "skill-sheet" ? "Skill sheet" : "Test"}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>

        <Box
          role="radio" tabIndex={0} aria-checked={kind === "document"}
          onClick={() => { if (kind !== "document") { setKind("document"); setFiles([]); } }}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); if (kind !== "document") { setKind("document"); setFiles([]); } } }}
          sx={{
            textAlign: "left", font: "inherit", color: "inherit", bgcolor: W.surface,
            border: `1.5px solid ${kind === "document" ? W.ink : W.line}`,
            boxShadow: kind === "document" ? `inset 0 0 0 1px ${W.ink}` : "none",
            borderRadius: "12px", p: "16px", cursor: "pointer", display: "flex", gap: "12px",
            "&:hover": { bgcolor: "#faf9fb" },
          }}
        >
          <Box sx={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${kind === "document" ? W.ink : W.lineCool}`, mt: "2px", flexShrink: 0, display: "grid", placeItems: "center" }}>
            {kind === "document" && <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: W.ink }} />}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: "15.5px", fontWeight: 600 }}>Document</Typography>
            <Typography sx={{ fontSize: "13px", color: W.ink3, mt: "3px", lineHeight: 1.45 }}>
              Store the file as-is in the Document Library. No scanning.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        role="button" tabIndex={0}
        onClick={() => inputRef.current.click()}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current.click()}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        sx={{
          border: `1.5px dashed ${over ? (kind === "ai" ? W.ai : W.ink) : W.lineCool}`,
          bgcolor: over ? (kind === "ai" ? W.aiBg : "#faf9fb") : "transparent",
          borderRadius: "12px", p: "26px 20px", display: "flex", flexDirection: "column",
          alignItems: "center", gap: "8px", textAlign: "center", cursor: "pointer",
        }}
      >
        <Box sx={{ width: 42, height: 42, borderRadius: "50%", bgcolor: W.surface2, display: "grid", placeItems: "center" }}>
          <Box component="svg" width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="m17 8-5-5-5 5" /><path d="M12 3v12" />
          </Box>
        </Box>
        <Typography sx={{ fontSize: "14.5px", fontWeight: 600 }}>Drop files here or browse</Typography>
        <Typography sx={{ fontSize: "12.5px", color: W.ink3 }}>{kind === "ai" ? AI_HINT : DOC_HINT}</Typography>
        <input
          ref={inputRef} type="file" hidden multiple
          accept={kind === "ai" ? AI_EXTENSIONS.map((e) => `.${e}`).join(",") : undefined}
          onChange={(e) => { addFiles([...e.target.files]); e.target.value = ""; }}
        />
      </Box>

      {files.length > 0 && (
        <Box sx={{ border: `1px solid ${W.line}`, borderRadius: "10px", overflow: "hidden" }}>
          {files.map((f, i) => (
            <Box key={`${f.name}-${i}`} sx={{ display: "grid", gridTemplateColumns: "36px minmax(0,1fr) auto", gap: "12px", alignItems: "center", p: "10px 14px", borderTop: i ? `1px solid ${W.line}` : "none" }}>
              <Box sx={{ width: 36, height: 36, borderRadius: "8px", bgcolor: W.surface2, display: "grid", placeItems: "center", fontSize: "10px", fontWeight: 700, fontFamily: "var(--font-mono)" }}>{extOf(f.name)}</Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: "14px", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</Typography>
                <Typography sx={{ fontSize: "12.5px", color: W.ink3 }}>{fmtSize(f.size)}</Typography>
              </Box>
              <Box component="button" type="button" aria-label={`Remove ${f.name}`} onClick={() => removeFile(i)}
                sx={{ width: 30, height: 30, borderRadius: "8px", border: `1px solid ${W.line}`, bgcolor: "#fff", display: "grid", placeItems: "center", cursor: "pointer", color: W.ink3, "&:hover": { bgcolor: W.surface2 } }}>
                ✕
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {kind === "ai" && files.length > 0 && (
        <Box sx={{ p: "12px 14px", borderRadius: "10px", bgcolor: W.aiBg, border: `1px solid ${W.aiLine}`, fontSize: "13.5px", lineHeight: 1.45 }}>
          Each file is scanned with AI and turned into a draft {aiType === "test" ? "test" : "skill sheet"}. You'll review every step before anything is saved.
        </Box>
      )}
    </Box>
  );
}
